import { supabase } from "@/integrations/supabase/client";
import type { Customer, ScanResult } from "../types/customer";
import type { Job } from "../types/job";

export type AdminState = {
  customers: Customer[];
  scanResults: ScanResult[];
  jobs: Job[];
};

const supabaseAnonKey =
  (import.meta as unknown as { env?: Record<string, string> }).env
    ?.VITE_SUPABASE_PUBLISHABLE_KEY ||
  (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_ANON_KEY ||
  (import.meta as unknown as { env?: Record<string, string> }).env?.PUBLIC_SUPABASE_ANON_KEY ||
  (import.meta as unknown as { env?: Record<string, string> }).env?.SUPABASE_ANON_KEY ||
  "";

export function apiBaseUrl(): string {
  const base =
    (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_AUTOSCAN_API_BASE_URL || "/api";
  if (typeof base !== "string") return "/api";
  let normalized = base.replace(/\/+$/, "");
  if (normalized !== "/api" && normalized.endsWith("/api")) {
    normalized = normalized.slice(0, -4);
  }
  if (normalized && normalized !== "/api") return normalized;
  if (typeof window !== "undefined") {
    const { origin, hostname } = window.location;
    if (hostname.endsWith("shadowscan.com.au")) {
      return `${origin}/admin`;
    }
  }
  return "/api";
}

function internalBaseUrl(): string {
  const base =
    (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_AUTOSCAN_INTERNAL_BASE_URL;
  if (typeof base === "string" && base.trim()) {
    return base.replace(/\/+$/, "");
  }
  if (typeof window !== "undefined") {
    const { origin, hostname } = window.location;
    if (hostname.endsWith("shadowscan.com.au")) {
      return `${origin}/admin`;
    }
    if (hostname.endsWith("lovable.app") || hostname.endsWith("lovableproject.com")) {
      return "https://shadowscan.com.au/admin";
    }
    return origin;
  }
  return "";
}

export function apiUrl(path: string): string {
  let normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (normalizedPath === "/internal" || normalizedPath.startsWith("/internal/")) {
    const base = internalBaseUrl();
    return base ? `${base}${normalizedPath}` : normalizedPath;
  }
  const base = apiBaseUrl();
  if (base.endsWith("/api")) {
    if (normalizedPath === "/api") {
      normalizedPath = "";
    } else if (normalizedPath.startsWith("/api/")) {
      normalizedPath = normalizedPath.slice(4) || "/";
    }
  }
  return `${base}${normalizedPath}`;
}

export async function fetchApi(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {});
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;

  if (accessToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (supabaseAnonKey && !headers.has("apikey")) {
    headers.set("apikey", supabaseAnonKey);
  }

  return fetch(apiUrl(path), { credentials: "include", ...init, headers });
}

export async function fetchApiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetchApi(path, init);
  const raw = await res.text();
  const preview = raw.slice(0, 200).replace(/\s+/g, " ").trim();

  if (!res.ok) {
    throw new Error(
      `Request failed (${res.status}) for ${res.url || apiUrl(path)}${preview ? `: ${preview}` : ""}`,
    );
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    throw new Error(
      `Expected JSON but got ${preview ? JSON.stringify(preview) : "a non-JSON response"} for ${res.url || apiUrl(path)}`,
    );
  }
}

export async function fetchAdminState(): Promise<AdminState> {
  const data = await fetchApiJson<Partial<AdminState>>("/internal/admin/state");
  return {
    customers: data.customers || [],
    scanResults: data.scanResults || [],
    jobs: data.jobs || [],
  };
}

export async function assertApiOk(res: Response, label = "Request"): Promise<void> {
  if (res.ok) return;
  const raw = await res.text().catch(() => "");
  const preview = raw.slice(0, 200).replace(/\s+/g, " ").trim();
  throw new Error(`${label} failed (${res.status})${preview ? `: ${preview}` : ""}`);
}
